// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { TextStyle } from 'react-native';

/** The parts of a button label a skin may set; typed so other skins can pick other values. */
export type ButtonLabelStyle = Pick<TextStyle, 'fontSize' | 'fontWeight' | 'letterSpacing' | 'textTransform'>;

/** Painted image assets on the score input, or controls drawn from the palette. */
export type ScoreInputChrome = 'artwork' | 'drawn';

/**
 * The original Goffy skin: dark green surfaces, beige text, Sanchez headlines.
 *
 * A skin is the complete set of colours, fonts and map colours the UI reads through
 * theme/colors.ts, theme/typography.ts and theme/map.ts. This one defines the shape (see
 * `Skin` in theme/skin.ts); every other skin must provide every key of it.
 */
export const classicSkin = {
  colors: {
    background: {
      app: '#011806',
      panel: '#07200D',
      panelAlt: '#0C2510',
      elevated: '#123018',
      deep: '#04210D',
      interactive: '#A1D493',
      interactiveMuted: '#2A4A2F',
    },
    surface: {
      base: '#07200D',
      alt: '#0C2510',
      elevated: '#123018',
      input: '#0D2A13',
      muted: '#183520',
      card: '#0B2A14',
      light: '#F6F8F6',
      toggleOff: '#3F5A45',
      // Translucent green washes; use with alpha().
      tint: '#1D4625',
      tintMid: '#143E1F',
      tintDeep: '#112C1B',
      // Warm brown surfaces: the membership editor's pending changes and the hole screen's cards.
      brown: '#3B2514',
      brownLight: '#5A3A22',
      brownDeep: '#29170B',
      brownDark: '#1A120D',
    },
    text: {
      primary: '#F5F5DC',
      secondary: '#C8C8B0',
      muted: '#8FA08B',
      heading: '#F5F5DC',
      inverse: '#011806',
      hint: '#A1B49E',
      subtle: '#BFD0BC',
      faint: '#8FA88F',
      dim: '#5F7E5A',
      accentMuted: '#7FA978',
      accentSoft: '#C6E3B8',
      positive: '#D4F5DA',
      warm: '#F6E5C0',
      onLight: '#4A4A4A',
      slateMuted: '#8FA3B8',
      // Ink that flips with the skin, by role: placeholder text, a label on a strong fill.
      placeholder: '#8D9C98',
      onStrong: '#FFFFFF',
      // Ink over photos, video and the map: white in every skin, on purpose.
      onMedia: '#FFFFFF',
    },
    border: {
      default: 'rgba(22,47,26,0.15)',
      strong: '#1A3A20',
      accent: '#A1D493',
      accentMuted: '#1E4D22',
      onLight: '#D2D6DC',
      brown: '#92400E',
      // Low-contrast rules on panels; they flip with the skin, unlike a white at low alpha.
      hairline: 'rgba(255,255,255,0.12)',
      subtle: 'rgba(226,232,240,0.35)',
    },
    neutral: {
      white: '#FFFFFF',
      black: '#000000',
      // Slate text and hairlines on media and dark overlays; use with alpha().
      slate: '#E2E8F0',
      navy: '#111827',
      navyDeep: '#0C1435',
      // Near-black scrim behind modals; use with alpha().
      ink: '#040711',
      grey: '#6B7280',
      greyDark: '#5B5B5B',
      charcoal: '#2C2C2C',
    },
    accent: {
      primary: '#A1D493',
      positive: '#A1D493',
      secondary: '#8BCF8A',
      light: '#AEE79A',
      soft: '#8ECF9A',
      lime: '#9FCC15',
      teal: '#2D6B66',
      tealDeep: '#163F4D',
      indigo: '#33456D',
      // Tonal accent border; use with alpha().
      glow: '#2A7742',
      subtleBg: '#0C2510',
      subtleBorder: 'rgba(161,212,147,0.3)',
      overlay: 'rgba(1,24,6,0.5)',
      gold: '#F2C94C',
      goldDeep: '#E0A82E',
      goldMuted: '#D7B65C',
      goldSoft: 'rgba(242,201,76,0.16)',
      goldBorder: 'rgba(242,201,76,0.45)',
      // Eclectic Freeze ice blue, matching GoffyColors.FreezeBlue on the watch.
      freeze: '#BFE6FF',
      freezeMuted: 'rgba(191,230,255,0.55)',
      blue: '#4A9BFF',
      blueSoft: '#93C5FD',
      blueDeep: '#1C4ED8',
      blueMuted: '#2B6CB0',
      mint: '#34D69D',
      mintDeep: '#1AA374',
      mintSoft: '#85F0C1',
      limeSoft: '#D9F99D',
      // Brighter tonal wash behind selected cards; use with alpha().
      glowBright: '#2F8A4E',
      olive: '#D1D27B',
      bronze: '#CD8B52',
      orange: '#EF6C00',
      purple: '#5B4A7A',
    },
    // Series and marker colours for charts and bars, distinct from the UI palette on purpose.
    chart: {
      pink: '#F9A8D4',
      peach: '#F0997B',
      salmon: '#E0A18A',
      sand: '#F6D98A',
      sky: '#85B7EB',
    },
    status: {
      danger: '#d73f4e',
      dangerStrong: '#bf2634',
      dangerText: '#FCA5A5',
      dangerTextSoft: '#FECACA',
      dangerBg: '#7F1D1D',
      dangerBgMuted: '#7A2E2E',
      errorText: '#FF7B7B',
      errorTextStrong: '#FF6666',
      errorTextSoft: '#FF9FA6',
      warning: '#a66f11',
      warningText: '#FCD34D',
      warningBorder: '#F59E0B',
      warningBg: '#6B5A1E',
      success: '#A1D493',
      successBg: '#1F5B2E',
      successStrong: '#16A34A',
      dangerVivid: '#EF4444',
      dangerBgStrong: '#991B1B',
      dangerPanel: '#2B0B0B',
      errorTextMuted: '#E58F8F',
      warningStrong: '#E68A00',
      warningBorderMuted: '#8D6232',
    },
    button: {
      primaryBg: '#A1D493',
      primaryBorder: '#A1D493',
      primaryText: '#011806',
      secondaryBg: '#0C2510',
      secondaryBorder: '#1A3A20',
      secondaryText: '#F5F5DC',
      ghostBg: 'transparent',
      ghostBorder: '#1A3A20',
      ghostText: '#C8C8B0',
    },
  },
  // The bundled family names must match the fonts loaded in app/_layout.tsx.
  fonts: {
    brand: 'Sanchez',
    mono: 'SpaceMono',
    // Editorial headline stack on web, and the platform fallbacks where no bundled font is used.
    webSerif: 'Newsreader, Playfair Display, Georgia, serif',
    systemSerif: 'serif',
    systemMono: 'monospace',
  },
  // The shape of GoffyButton: classic is a stamped label, small tracked capitals in a wide
  // rounded box. Sizes stay in the component; a skin sets the corner, the border and the label.
  button: {
    radius: 16,
    borderWidth: 1,
    label: { fontSize: 12, fontWeight: '800', letterSpacing: 2.1, textTransform: 'uppercase' } as ButtonLabelStyle,
  },
  // Which chrome the hole score entry wears. 'artwork' is the painted set — wooden plank
  // buttons, the tee-peg rail and the hazard animals, all image assets served from S3.
  // 'drawn' builds the same controls out of the palette instead, for a skin the artwork
  // was never painted for. It is a per-skin choice because the assets are one fixed set:
  // a paper-and-ink skin cannot recolour a photograph of wood.
  chrome: {
    scoreInput: 'artwork' as ScoreInputChrome,
  },
  // Colours of the hole map itself: map semantics, kept apart from the UI palette so a skin
  // can restyle the chrome without repainting the course, and the other way round.
  map: {
    /**
     * Boundary colours drawn over satellite imagery: a stroke with the same colour behind it at
     * low opacity, bright enough to read against grass, water and sand alike.
     */
    overlay: {
      bunker: '#F0C450',
      water: '#3884FF',
      green: '#3CC86E',
      fairway: '#9BE87A',
      trees: '#1F6B3A',
      // The laser's canopy stands, not a detected tree area: a paler, cooler green so the two
      // read as different claims where they overlap — which is most of the time, since one is
      // the evidence for the other (see CanopySignal in geodata-service).
      canopy: '#7FB8A0',
      // A road or cart path from the OSM import: the analyser's negative mask, drawn on the
      // admin map so a bunker that vanished can be seen to have been on a road.
      path: '#C9C9C9',
    },
    /** Flat cartographic fills, in the spirit of a paper yardage book rather than the imagery. */
    drawing: {
      trees: { fill: '#1E3A24', stroke: '#16281A' },
      fairway: { fill: '#4E8C46', stroke: '#3F7139' },
      water: { fill: '#2C6FA8', stroke: '#1E4F79' },
      bunker: { fill: '#D9C58E', stroke: '#B39F68' },
      green: { fill: '#7FC46A', stroke: '#5B9A4B' },
      // Roads and cart paths: a neutral light grey, cooler than the bunker's sand so the two
      // never read as the same thing, and pale enough to show against rough and fairway.
      path: { fill: '#BDBBB5', stroke: '#9C9A94' },
    },
    /** What a drawing shows where nothing else is mapped: everything is rough until proven otherwise. */
    rough: '#2F5B33',
    /** The player's position and the green marker on the canvas. */
    marker: '#7ED957',
  },
};
